import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Eye, Wrench, CheckCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API, AXIOS_SKIP_HTTP_CACHE, ORDERS_LIST_PARAMS } from '../config';
import { isAdmin } from '../utils/roles';
import { orderMatchesGlobalSearch } from '../utils/listSearch';
import {
  countServicebonArchiveRounds,
  getClosedOrderCompletedTime,
  getPendingOfficeAttention,
  getServicebonAttentionKind,
  isOrderOnServicePageActiveList,
  isOrderPendingOfficeCloseOnService,
  isOrderWithServicebonArchive,
} from '../utils/orderAttention';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';

function formatClosedOrderAddress(order) {
  const street = String(order?.address ?? '').trim();
  const postcode = String(order?.postcode ?? '').trim();
  if (!street && !postcode) return null;
  if (!postcode) return { line1: street, line2: null };
  if (!street) return { line1: postcode, line2: null };
  return { line1: `${street},`, line2: postcode };
}

function formatServiceAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(n);
}

function ServiceAmountCell({ order, editable, onSaved, t }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formatServiceAmount(order.service_confirm_amount));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(formatServiceAmount(order.service_confirm_amount));
    }
  }, [order.service_confirm_amount, editing]);

  const commit = async () => {
    const parsed = Number(String(draft).replace(',', '.'));
    const next = Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) / 100 : 0;
    const prev = Number(order.service_confirm_amount || 0);
    setEditing(false);
    setDraft(formatServiceAmount(next));
    if (next === prev) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/orders/${order.id}/service-confirm-amount`,
        { amount: next },
        { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE },
      );
      onSaved?.(data || { ...order, service_confirm_amount: next });
      toast.success(t('serviceAmountSaved'));
    } catch (e) {
      setDraft(formatServiceAmount(order.service_confirm_amount));
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('serviceAmountSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!editable) {
    return (
      <span className="text-sm tabular-nums text-[#64748b]">
        € {formatServiceAmount(order.service_confirm_amount)}
      </span>
    );
  }

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        step="0.01"
        autoFocus
        disabled={saving}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            setDraft(formatServiceAmount(order.service_confirm_amount));
            setEditing(false);
          }
        }}
        className="vmk-input !w-24 !py-1 !px-2 !text-sm tabular-nums"
        data-testid={`service-amount-input-${order.id}`}
        aria-label={t('serviceAmountColumn')}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="min-w-[4.5rem] rounded-md border border-transparent px-2 py-1 text-left text-sm tabular-nums text-[#1a1a1a] hover:border-[#00CBA9]/50 hover:bg-[#00CBA9]/5 dark:text-[#f1f5f9]"
      title={t('serviceAmountColumn')}
      data-testid={`service-amount-cell-${order.id}`}
    >
      € {formatServiceAmount(order.service_confirm_amount)}
    </button>
  );
}

function serviceStatusLabel(order, t) {
  const mobile = String(order.mobile_work_status || '').trim().toLowerCase();
  if (getPendingOfficeAttention(order) && mobile === 'completed') {
    return t('orderStatusAwaitingBalance');
  }
  if (mobile === 'awaiting_servicebon') {
    const kind = getServicebonAttentionKind(order);
    if (kind === 'need_web_text') return t('serviceStatusShortNeedText');
    if (kind === 'signed_mobile') return t('serviceStatusShortSigned');
    return t('serviceStatusShortAwaiting');
  }
  const kind = getServicebonAttentionKind(order);
  if (kind === 'need_web_text') return t('serviceStatusShortNeedText');
  if (kind === 'signed_mobile') return t('serviceStatusShortSigned');
  return t('serviceStatusShortProgress');
}

const ServicePage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canMarkComplete = isAdmin(user);
  const canReopen = isAdmin(user);
  const [orders, setOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [section, setSection] = useState('active');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE, ...ORDERS_LIST_PARAMS }),
        axios.get(`${API}/teams`, { withCredentials: true }),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
    } catch {
      toast.error(t('failedFetchData'));
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadData();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  const getTeamName = (teamId) => {
    const team = teams.find((t2) => String(t2.id) === String(teamId));
    return team?.name || '-';
  };

  const activeServiceOrders = useMemo(
    () => orders.filter((o) => isOrderOnServicePageActiveList(o)),
    [orders],
  );

  const archivedServiceOrders = useMemo(() => {
    const list = orders.filter((o) => isOrderWithServicebonArchive(o));
    return [...list].sort(
      (a, b) => getClosedOrderCompletedTime(b) - getClosedOrderCompletedTime(a),
    );
  }, [orders]);

  const listForSection = section === 'active' ? activeServiceOrders : archivedServiceOrders;

  const filteredOrders = useMemo(
    () => listForSection.filter((o) => orderMatchesGlobalSearch(o, searchQuery)),
    [listForSection, searchQuery],
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize, section]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredOrders.length / pageSize)),
    [filteredOrders.length, pageSize],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, page, pageSize]);

  const openServicebon = (orderId, from) => {
    navigate(
      `/orders?view=${encodeURIComponent(orderId)}&from=${from}&tab=servicebon`,
    );
  };

  const handleMarkComplete = async (order) => {
    if (!window.confirm(t('confirmFinalizeOrderAfterBalance'))) return;
    try {
      await axios.put(`${API}/orders/${order.id}/complete-after-balance`, {}, { withCredentials: true });
      toast.success(t('orderFinalizedSuccess'));
      await loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('orderFinalizeFailed'));
    }
  };

  const handleReopen = async (order) => {
    if (!window.confirm(t('reopenOrderConfirm'))) return;
    try {
      const { data } = await axios.put(
        `${API}/orders/${order.id}/reopen-completion`,
        {},
        { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE },
      );
      if (data && data.id != null) {
        setOrders((prev) => prev.map((o) => (String(o.id) === String(data.id) ? { ...o, ...data } : o)));
      }
      toast.success(t('reopenOrderSuccess'));
      if (data?.id != null) {
        navigate(`/orders?view=${data.id}&from=service&tab=servicebon`);
      } else {
        navigate('/service');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('reopenOrderFailed'));
    }
  };

  const handleAmountSaved = (updated) => {
    if (!updated?.id) return;
    setOrders((prev) =>
      prev.map((o) => (String(o.id) === String(updated.id) ? { ...o, ...updated } : o)),
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="service-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Wrench size={28} weight="duotone" className="text-[#00CBA9]" />
            {t('service')}
          </h1>
          <p className="page-subtitle">{t('servicePageSubtitle')}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          className={`vmk-btn text-sm ${section === 'active' ? 'vmk-btn-primary' : ''}`}
          onClick={() => setSection('active')}
        >
          {t('serviceActiveSection')} ({activeServiceOrders.length})
        </button>
        <button
          type="button"
          className={`vmk-btn text-sm ${section === 'archive' ? 'vmk-btn-primary' : ''}`}
          onClick={() => setSection('archive')}
        >
          {t('serviceArchiveSection')} ({archivedServiceOrders.length})
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-[#94a3b8]"
            aria-hidden
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('searchOrders')}
            className="vmk-input !pl-[2.75rem] !pr-4"
          />
        </div>
        {filteredOrders.length > 25 && (
          <div className="w-[170px]">
            <Select
              value={String(pageSize)}
              onValueChange={(v) => setPageSize(Number(v) || 25)}
            >
              <SelectTrigger className="vmk-input h-11 border-[#e2e8f0] bg-white pl-3 pr-2">
                <span className="text-xs font-semibold text-[#64748b] mr-2">
                  {t('rowsPerPage')}
                </span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {filteredOrders.length === 0 ? (
        <p className="text-sm text-[#64748b] py-8 text-center">
          {section === 'active' ? t('serviceNoActive') : t('serviceNoArchive')}
        </p>
      ) : (
        <div className="table-container">
          <table className="vmk-table">
            <thead>
              <tr>
                <th>{t('client')}</th>
                <th>{t('address')}</th>
                <th>{t('team')}</th>
                <th>{t('offerNumber')}</th>
                <th>{t('serviceAmountColumn')}</th>
                <th>{section === 'active' ? t('serviceStatusColumn') : t('serviceRoundsColumn')}</th>
                <th className="text-right">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pagedOrders.map((order) => (
                <tr key={order.id}>
                  <td>{order.client_name || '-'}</td>
                  <td className="align-top whitespace-pre-line">
                    {(() => {
                      const addr = formatClosedOrderAddress(order);
                      if (!addr) return '-';
                      return (
                        <>
                          <div className="text-sm text-[#1a1a1a] dark:text-[#f1f5f9]">{addr.line1}</div>
                          {addr.line2 ? (
                            <div className="text-xs text-[#94a3b8]">{addr.line2}</div>
                          ) : null}
                        </>
                      );
                    })()}
                  </td>
                  <td>{getTeamName(order.assigned_team_id)}</td>
                  <td>{order.offer_id || '-'}</td>
                  <td>
                    <ServiceAmountCell
                      order={order}
                      editable={section === 'active'}
                      onSaved={handleAmountSaved}
                      t={t}
                    />
                  </td>
                  <td>
                    {section === 'active' ? (
                      <span className="status-badge in-progress text-xs">
                        {serviceStatusLabel(order, t)}
                      </span>
                    ) : (
                      <span className="text-sm text-[#64748b]">
                        {t('serviceRoundCount', countServicebonArchiveRounds(order))}
                      </span>
                    )}
                  </td>
                  <td className="table-actions-cell">
                    <div className="table-row-actions">
                      {canMarkComplete &&
                      section === 'active' &&
                      isOrderPendingOfficeCloseOnService(order) ? (
                        <button
                          type="button"
                          onClick={() => void handleMarkComplete(order)}
                          className="vmk-btn text-sm py-2 border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-950/70"
                          title={t('markOrderCompleteAfterBalance')}
                        >
                          <CheckCircle size={16} weight="bold" />
                          {t('markOrderCompleteAfterBalance')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() =>
                          openServicebon(
                            order.id,
                            section === 'active' ? 'service' : 'service-archive',
                          )
                        }
                        className="vmk-btn vmk-btn-icon"
                        title={t('serviceOpenServicebon')}
                        aria-label={t('serviceOpenServicebon')}
                      >
                        <Eye size={16} weight="bold" />
                      </button>
                      {canReopen && section === 'archive' ? (
                        <button
                          type="button"
                          onClick={() => void handleReopen(order)}
                          className="vmk-btn vmk-btn-icon"
                          title={t('historyRevoke')}
                          aria-label={t('historyRevoke')}
                        >
                          <ArrowCounterClockwise size={16} weight="bold" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredOrders.length > pageSize && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#64748b]">
            {t('page')} {page} / {totalPages}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink isActive>{page}</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ServicePage;
